import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { useRouter } from 'next/router';
import React, { useContext, useRef } from 'react';
import { useRecoilState } from 'recoil';

import { AxiosStatusCode } from '@asa/asasqt-microfrontend-components/dist/common/api/axios/utils/types';
import {
  PORTAL_ROUTES,
  PORTAL_ROUTES_NAMES,
} from '@asa/asasqt-microfrontend-components/dist/common/api/myAsaSqt';
import {
  shouldUseRememberMe,
  tokenAtom,
} from '@asa/asasqt-microfrontend-components/dist/common/state/shared';
import { useRefreshToken } from '@asa/asasqt-microfrontend-components/dist/hooks/auth/useRefreshToken';

import { USER_24_SERVICES } from '@/core/common/api/ilias';
import { URLRoutes } from '@/core/common/helpers';

import { ILIAS_ROUTES, ILIAS_ROUTES_NAMES } from '../api/ilias';
import useLogout from '../hooks/useLogout';
import { languageAtom } from '../language/core/language.recoil';
import { IProps } from './types';

export const AxiosContext = React.createContext({} as AxiosInstance);

export function useAxiosIntercept() {
  return useContext(AxiosContext);
}

const MAX_RETRY = 2;

export function AxiosContextProvider({ children }: IProps) {
  const { initiateLogoutWithAxiosInstance } = useLogout();
  const [token] = useRecoilState(tokenAtom);
  const [language, setLanguage] = useRecoilState(languageAtom);
  const [useRememberMe, setUseRememberMe] = useRecoilState(shouldUseRememberMe);

  const router = useRouter();
  const axiosToken = token;
  const isRefreshing = useRef<boolean>(false);
  const refreshPromise = useRef<Promise<string> | null>(null);
  const isHandling401 = useRef<boolean>(false);

  const axiosIntercept = axios.create({
    timeout: 35000,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept-Language': language,
    },
  });
  const { refreshToken } = useRefreshToken({
    axiosIntercept,
    USER_24_SERVICES,
  });

  axiosIntercept.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (
        typeof document !== 'undefined' &&
        document.hidden &&
        config.url !== ILIAS_ROUTES[ILIAS_ROUTES_NAMES.AUTHORIZATION]
      ) {
        return Promise.reject(
          new axios.CanceledError('Request cancelled: page not visible')
        );
      }

      if (
        axiosToken &&
        config.url !== ILIAS_ROUTES[ILIAS_ROUTES_NAMES.AUTHORIZATION]
      ) {
        const jwt = JSON.parse(atob(axiosToken.split('.')[1]));
        const axiosTokenExpirationDate = jwt && jwt.exp && jwt.exp * 1000;
        const isTokenExpiringSoon =
          axiosTokenExpirationDate - 60000 < Date.now();

        let activeToken = axiosToken;

        if (isTokenExpiringSoon) {
          if (!isRefreshing.current) {
            isRefreshing.current = true;
            refreshPromise.current = refreshToken();
            try {
              const newToken = await refreshPromise.current;
              if (newToken) activeToken = newToken;
            } finally {
              isRefreshing.current = false;
              refreshPromise.current = null;
            }
          } else if (refreshPromise.current) {
            try {
              const newToken = await refreshPromise.current;
              if (newToken) activeToken = newToken;
            } catch {}
          }
        }

        if (!config.headers?.Authorization) {
          config.headers['Authorization'] = `Bearer ${activeToken}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosIntercept.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.data?.language) {
        setLanguage(response.data?.language);
      }
      return response;
    },
    async (error) => {
      const config = error.config;

      if (axios.isCancel(error) || error.code === 'ERR_CANCELED' || !config) {
        return Promise.reject(error);
      }

      if (config?.handled503) {
        return Promise.reject(error);
      }

      // Handling 503 Service Unavailable
      if (error?.response?.status === 503) {
        config.retry503Count = (config.retry503Count || 0) + 1;
        if (config.retry503Count <= 3) {
          return axiosIntercept(config);
        } else {
          console.error('Handling 503: Redirecting after 3 retries');
          return Promise.reject(
            new Error('503 Service Unavailable - handled after retries')
          );
        }
      }

      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        // Authorization timed out → fail fast. Retrying after 35s of unavailability
        // doubles the cascade depth when combined with the notification poll retry cycle.
        if (
          config.url === ILIAS_ROUTES[ILIAS_ROUTES_NAMES.AUTHORIZATION] ||
          config?.url?.includes(
            PORTAL_ROUTES[PORTAL_ROUTES_NAMES.COMMANDS_AVAILABLE]
          )
        ) {
          return Promise.reject(error);
        }

        if (config._retryCount === undefined) {
          // Creating a _retryCount property in the config object and initializing it to 1
          config._retryCount = 1;
        }
        if (config._retryCount < MAX_RETRY) {
          // If it's already failed once, increment the _retryCount
          config._retryCount++;
        } else {
          return Promise.reject(error);
        }
      }

      if (config?._retryCount <= MAX_RETRY) {
        // Retry request, if _retryCount is less than or equal to MAX_RETRY
        return axiosIntercept(config);
      }

      if (error?.response?.status === AxiosStatusCode.ERR_401) {
        if (
          error?.config?.url === ILIAS_ROUTES[ILIAS_ROUTES_NAMES.LOGIN] ||
          error?.config?.url?.includes(
            ILIAS_ROUTES[ILIAS_ROUTES_NAMES.REMEMBER_ME]
          ) ||
          error?.config?.url?.includes(
            PORTAL_ROUTES[PORTAL_ROUTES_NAMES.COMMANDS_AVAILABLE]
          )
        ) {
          setUseRememberMe(false);
          return;
        }

        if (
          error?.config?.url === ILIAS_ROUTES[ILIAS_ROUTES_NAMES.AUTHORIZATION]
        ) {
          isHandling401.current = false;
          logout();
          return;
        }

        if (isHandling401.current) {
          return Promise.reject(error);
        }

        isHandling401.current = true;

        if (!useRememberMe) {
          setUseRememberMe(true);
        }

        if (
          isRefreshing.current &&
          refreshPromise.current !== null &&
          !error?.config['X-Retry']
        ) {
          try {
            const refreshedToken = await refreshPromise.current;
            const newConfig = error?.config;
            newConfig.headers.Authorization = `Bearer ${refreshedToken}`;
            newConfig.headers['X-Retry'] = true;
            isHandling401.current = false;
            return axiosIntercept.request(newConfig);
          } catch {
            isHandling401.current = false;
          }
        }

        if (!router.pathname.includes(URLRoutes.auth)) {
          logout(true);
        }
      }

      return Promise.reject(error);
    }
  );

  const logout = (withQuery: boolean = false) => {
    initiateLogoutWithAxiosInstance(axiosIntercept, true, withQuery);
  };

  return (
    <AxiosContext.Provider value={axiosIntercept}>
      {children}
    </AxiosContext.Provider>
  );
}
